import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'shared/api-client';
import { API } from 'shared/config';
import type { ConsentClusterSlug, ConsentKindSlug } from 'shared/schemas/common';
import { useAuthTokens } from 'shared/tokens';
import type { MaybePromise } from 'shared/types';
import * as v from 'valibot';

import routes from '@/assets/constants/routes-no.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { clearStorage } from '@/lib/clearStorage.ts';
import { useAuth } from '@/services/Auth/index.ts';

import { hardNavigate } from '../utils.ts';

import { meQuery, MeSchema } from './queries.ts';

export type UpdateProfileArgs = {
  email?: string;
  phone_number?: string;
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: UpdateProfileArgs) => {
      const res = await apiClient.patch('account/me/', { json: args }).json();
      return v.parse(MeSchema, res);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQuery().queryKey });
    },
  });
};

export const useLogout = () => {
  const tokens = useAuthTokens();

  return useMutation({
    mutationFn: async () => {
      // Blacklists the tokens
      await apiClient.post('account/logout/', {
        json: { access: tokens?.accessToken, refresh: tokens?.refreshToken },
      });
    },
    onSettled: async () => {
      clearStorage();
      sendUserPortalEvent('logout');

      // Refreshing page will clear all local state
      // Removes cookies and redirects to the logged out page
      await hardNavigate(
        `${API.url}/account/logout/?redirect_url=${window.location.origin}${routes.LOGGED_OUT}`,
      );
    },
  });
};

export const useDeleteAccount = () => {
  const { userId } = useAuth();
  const { mutate: logout } = useLogout();

  return useMutation({
    mutationFn: async () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      await apiClient.delete('account/delete/');
      sendUserPortalEvent('account_deleted', { user_id: userId });
    },
    onSuccess: async () => {
      logout();
    },
  });
};

export const useApplyConsentClusters = (opts?: { onSuccess?: () => MaybePromise<void> }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (consents: ConsentClusterSlug[]) => {
      await apiClient.patch('bi/consent-clusters/apply_consents/', {
        json: { slugs: consents },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['consents'] });
      await opts?.onSuccess?.();
    },
  });
};

export const useApplyConsentKinds = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (consents: ConsentKindSlug[]) => {
      await apiClient.patch('bi/api/consents/apply_consents/', { json: { slugs: consents } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['consents'] });
    },
  });
};

export const useRevokeConsentKinds = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (consents: ConsentKindSlug[]) => {
      await apiClient.patch('bi/api/consents/revoke_consents/', { json: { slugs: consents } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['consents'] });
    },
  });
};
